/**
 * Creates an array of string or number which contains only unique items
 * @param list array of string or number
 * @result array of string or number
 * @example
 *
 * const uniques = uniqueArray([1,1,1,3,2,1]); // -> [1,3,2]
 */
export const uniqueArray = <T extends string | number>(list: Array<T>): Array<T> => {
  return [...Array.from(new Set(list))];
};

/**
 * Creates a unique array of items by a key
 * @param list array of objects
 * @param key key of the object
 */
export const uniqueArrayBy = <T, K extends keyof T>(list: Array<T>, key: K): Array<T[K]> => {
  return [...Array.from(new Set(list.map(item => item[key])))];
};

/**
 * Returns the min number from an array where elements can be undefined
 * @param values array of number and undefined elements
 * @returns min number if the array contains a number undefined otherwise
 */
export const getMinIfDefined = (...values: Array<number | undefined>) => {
  return values.reduce((acc, current) => (acc && current ? Math.min(acc, current) : acc ?? current), undefined);
};

/**
 * Returns the max number from an array where elements can be undefined
 * @param values array of number and undefined elements
 * @returns max number if the array contains a number undefined otherwise
 */
export const getMaxIfDefined = (...values: Array<number | undefined>) => {
  return values.reduce((acc, current) => (acc && current ? Math.max(acc, current) : acc ?? current), undefined);
};

/**
 * Can be used in reduce to unique array of items by a key
 * @param key key of the object
 */
export const uniqueArrayByReduceFn = <T, K extends keyof T>(key: K) => {
  const set = new Set<T[K]>();
  return (acc: Array<T>, item: T): Array<T> => {
    if (!set.has(item[key])) {
      set.add(item[key]);
      acc.push(item);
    }
    return acc;
  };
};

/**
 * Creates a new array by removing duplicate elements from the given array based on a specified key.
 *
 * @param {Array<T>} list - The array from which to remove duplicate elements.
 * @param {K} key - The key used to determine uniqueness of elements.
 * @returns {Array<T>} A new array without duplicate elements.
 *
 * @template T - The type of elements in the array.
 * @template K - The type of the key to determine uniqueness.
 */
export const removeDuplicatesByKey = <T, K extends keyof T>(list: Array<T>, key: K): Array<T> => {
  const map = new Map<T[K], T>();
  list.forEach(item => {
    map.set(item[key], item);
  });
  return Array.from(map.values());
};

/**
 * Removes elements from an array
 * @param list array of string or number
 * @param excludedItem a single item OR Array of items to be excluded
 * @result array of string or number
 * @example
 *
 * const list = [1,2,3,4,5];
 *
 * const primeList = excludeFromArray(list, 4); // [1,2,3,5]
 *
 * const evenList = excludeFromArray(list, [2,4]); // -> [1,3,5]
 *
 */
export const excludeFromArray = <T extends string | number>(list: Array<T>, excludedItem: T | Array<T>): Array<T> => {
  if (Array.isArray(excludedItem)) {
    return list.filter(item => !excludedItem.includes(item));
  }
  return list.filter(item => item !== excludedItem);
};

export const findById = <T extends { id: string }>(list: Array<T>, id: string): T | undefined => {
  return list.find(item => item.id === id);
};

export const isArrayEqual = <T extends any>(array1: Array<T>, array2: Array<T>): boolean => {
  if (array1.length !== array2.length) {
    return false;
  }
  if (array1.length === 0 && array2.length === 0) {
    return true;
  }
  // for primitive types we can just convert to set and compare
  if (typeof array1[0] !== 'object') {
    return new Set(array1).symmetricDifference(new Set(array2)).size === 0;
  }

  return JSON.stringify(array1) === JSON.stringify(array2);
};

/**
 *
 * @param array
 * @param field
 * @example
 * const array = [{a: 1}, {a: 1}, {a: 1}];
 * console.log(isArrayFieldEqual(array, 'a')); // -> true
 *
 * const array = [{a: 1}, {a: 2}, {a: 1}];
 * console.log(isArrayFieldEqual(array, 'a')); // -> false
 */
export const isArrayFieldEqual = <T extends any>(array: Array<T>, field: keyof T): boolean => {
  if (array.length === 0) {
    return true;
  }

  const firstType = array[0][field];
  return array.every(obj => obj[field] === firstType);
};

/**
 * Groups an array of objects by a key
 * @param list array of objects
 * @param key key of the object
 */
export const groupBy = <T extends { [key: string]: any }>(
  list: Array<T>,
  key: keyof T,
): { [key: string]: Array<T> } => {
  return list.reduce(
    (acc, item) => {
      const group = item[key];
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    },
    {} as { [key: string]: Array<T> },
  );
};

/**
 * Groups an array of objects by a key and takes the first element of the group
 * @param list array of objects
 * @param key key of the object
 */
export const groupByFlat = <T extends { [key: string]: any }>(list: Array<T>, key: keyof T): { [key: string]: T } => {
  const map = groupBy(list, key);
  return Object.keys(map).reduce(
    (acc, group) => {
      if (map[group].length > 1) {
        console.warn(`groupByFlat: group ${group} has more than one element`);
      }
      acc[group] = map[group][0];
      return acc;
    },
    {} as { [key: string]: T },
  );
};

export type MergeNumericValuesSettings<T, K = any> = {
  exclude?: Array<keyof T>;
  include?: Array<keyof T>;
  onlyFirstValueFields?: Array<keyof T>;
  avgFields?: Array<keyof T>;
  weightedAvgField?: keyof T;
  reducer?: (acc: T & K, item: T) => K;
};

/**
 * Merges numeric values of an array of objects
 * @param list array of objects
 * @param settings settings
 * @param settings.exclude exclude fields from the merge
 * @param settings.include include fields from the merge
 * @param settings.onlyFirstValueFields only keep the first value of the merge
 * @param settings.avgFields avarage the values of the merge
 * @param settings.weightedAvgField a field name to count the avgFields
 * @param settings.reducer a function for custom reducing the items - use this if you need a custom logic how to summarize, avg the fields
 *
 * @example
 * mergeNumericValues([{age: 10, distance: 0, name: 'John'}, {age: 20, distance: 10, name: 'John'}], {
 *   avgFields: ['age'],
 *   reducer: (acc, item) => {
 *     if (acc.distance > 0) {
 *       acc.distance = (acc.distance || 0) + item.distance;
 *     }
 *     return acc;
 *   },
 * });
 */
export const mergeNumericValues = <INP extends { [key: string]: any }, RES extends INP>(
  list: Array<INP>,
  settings?: MergeNumericValuesSettings<INP, RES>,
): RES => {
  const exclude = settings?.exclude || [];
  const include = settings?.include || [];
  const onlyFirstValueFields = settings?.onlyFirstValueFields || [];
  const weightedAvgField = settings?.weightedAvgField;
  const avgFields = settings?.avgFields || [];
  const avarages = avgFields.reduce(
    (acc, field) => {
      acc[field] = { count: 0, value: 0 };
      return acc;
    },
    {} as Record<keyof INP, { count: number; value: number }>,
  );

  return list.reduce((pRaw: RES, next: INP) => {
    const raw = settings?.reducer ? settings.reducer(pRaw, next) : pRaw;
    Object.keys(next).forEach(field => {
      if (next.hasOwnProperty(field)) {
        if (typeof next[field] === 'number' && !Number.isNaN(next[field])) {
          if (exclude.includes(field)) {
            return;
          }

          if (onlyFirstValueFields.includes(field)) {
            if (!raw.hasOwnProperty(field)) {
              (raw[field] as undefined | number) = next[field];
            }
            return;
          }
          if (avgFields.includes(field)) {
            avarages[field as keyof INP].count += weightedAvgField ? next[weightedAvgField] : 1;
            avarages[field as keyof INP].value += next[field] * (weightedAvgField ? next[weightedAvgField] : 1);
            return; // raw;
          }

          if (include.includes(field) || include.length === 0) {
            (raw[field] as undefined | number) = (raw[field] || 0) + next[field];
          }
        } else {
          (raw[field] as any) = next[field];
        }
      }
    });

    avgFields.forEach(field => (raw[field as keyof INP] = (avarages[field].value / avarages[field].count) as any));
    return raw;
  }, {} as RES);
};

/**
 * Groups an array of objects by a key and merges numeric values
 * @param list array of objects
 * @param groupByKey key of the object
 * @param settings settings
 * @param settings.exclude exclude fields from the merge
 * @param settings.include include fields from the merge
 * @param settings.onlyFirstValueFields only keep the first value of the merge
 * @param settings.avgFields avarage the values of the merge
 * @param settings.weightedAvgField a field name to count the avgFields
 */
export const groupByAndMergeNumericValues = <INP extends Record<string, any>, RES extends INP>(
  list: Array<INP>,
  groupByKey: string,
  settings?: MergeNumericValuesSettings<INP, RES>,
): Array<RES> => {
  const grouped = groupBy(list, groupByKey);
  return Object.keys(grouped).reduce((acc, group) => {
    const newItem = mergeNumericValues(grouped[group], settings);
    acc.push(newItem);
    return acc;
  }, [] as Array<RES>);
};

/**
 * Helper function to merge numeric values when the input object's depth is unknown
 * @param inputObjOrArray object to traverse
 * @param settings merge settings
 * @param path current path used for creating unique ids
 * @returns flattened array of objects with merged numeric values for each group
 */
const mergeNumericValuesRecursive = <INP extends Record<string, any>>(
  inputObjOrArray: { [keys: string]: any } | Array<INP>,
  settings?: MergeNumericValuesSettings<INP, any>,
  path: string = '',
): Array<{ [keys: string]: any; __groupById?: string }> => {
  if (Array.isArray(inputObjOrArray)) {
    if (inputObjOrArray.length > 1) {
      return { ...mergeNumericValues(inputObjOrArray, settings), __groupById: path };
    } else {
      return { ...inputObjOrArray[0], __groupById: path };
    }
  }

  // Traverse object recursively
  return Object.entries(inputObjOrArray).flatMap(entry =>
    mergeNumericValuesRecursive(entry[1], settings, `${path}_${entry[0]}`),
  );
};

/**
 * Group an array of objects by multiple keys and then merge the numeric values
 * @param inputList input array of object
 * @param groupByKeys array of keys to group by (and in which order)
 * @param settings merge settings
 * @param settings.exclude exclude fields from the merge
 * @param settings.include include fields from the merge
 * @param settings.onlyFirstValueFields only keep the first value of the merge
 * @param settings.avgFields avarage the values of the merge
 * @param settings.weightedAvgField a field name to count the avgFields
 * @returns list of grouped and merged objects
 */
export const groupByAndMergeNumericValuesMultipleKeys = <INP extends Record<string, any>, RES extends INP>(
  inputList: Array<INP>,
  groupByKeys: Array<keyof INP>,
  settings?: MergeNumericValuesSettings<INP, RES>,
): Array<RES & { __groupById: string }> => {
  const groupedObj: { [keys: string]: any } = {};

  // Group the list by the given keys (same order as the groupByKeys array)
  inputList.forEach(l => {
    let currentPath = groupedObj;
    groupByKeys.forEach((k, ind) => {
      const currentKey = String(l[k]);
      if (ind === groupByKeys.length - 1) {
        if (!(currentKey in currentPath)) {
          currentPath[currentKey] = [l];
        } else {
          currentPath[currentKey].push(l);
        }
        return;
      }
      if (!(currentKey in currentPath)) {
        currentPath[currentKey] = {};
      }
      currentPath = currentPath[currentKey];
    });
  });

  // Merge all the numeric values and flatten the object
  return mergeNumericValuesRecursive(groupedObj, settings) as Array<RES & { __groupById: string }>;
};

/**
 * Gets the last element of an array. The array cannot be non-empty.
 */
export const getLastElement = <T>(list: Array<T>): T => {
  if (list.length === 0) {
    throw new Error(`List is empty, cannot retrieve last element`);
  }
  return list[list.length - 1];
};

/**
 * Determines whether there is an intersection between two arrays.
 *
 * @param {T[]} array1 - The first array.
 * @param {T[]} array2 - The second array.
 * @returns {boolean} - True if there is an intersection, false otherwise.
 *
 * @template T
 */
export const hasIntersection = <T>(array1: T[], array2: T[]): boolean => {
  const smallerArray = array1.length < array2.length ? array1 : array2;
  const largerArray = array1.length < array2.length ? array2 : array1;
  const set = new Set(smallerArray);

  for (const item of largerArray) {
    if (set.has(item)) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if an array contains another array
 * @example
 * const array1 = [1,2,3,4,5];
 * const array2 = [1,2,3];
 *
 * console.log(isArrayContainsAnother(['a'],      ['a']));      // -> true
 * console.log(isArrayContainsAnother(['a', 'b'], ['a']));      // -> true
 * console.log(isArrayContainsAnother(['a'],      ['a', 'b'])); // -> false
 * console.log(isArrayContainsAnother(['a'],      ['b']));      // -> false
 *
 * @param array1
 * @param array2
 */
export const isArrayContainsAnother = <T>(array1: T[], array2: T[]): boolean => {
  if (array2.length > array1.length) {
    return false;
  }
  const set = new Set(array1);
  for (const item of array2) {
    if (!set.has(item)) {
      return false;
    }
  }
  return true;
};

/**
 * Sorts an array in ascending order using the provided sort function.
 *
 * @template T The type of the elements in the array.
 * @param {T[]} array The array to be sorted.
 * @param {(a: T, b: T) => number} [sortFunction] The function used to compare elements for sorting. If not provided, a default comparison function is used.
 * @returns {T[]} The sorted array.
 */
export const sortArray = <T>(array: T[], sortFunction?: (a: T, b: T) => number): T[] => {
  return [...array].sort((a, b) => (sortFunction ? sortFunction(a, b) : a > b ? 1 : -1));
};

type ObjectId = string | number;

/**
 * Helper function to determine whether the property value is unique in an array of obj
 * Filters out objects with the same ID
 *
 * @returns True if there is an other obj with different ID and matching property value
 */
const doesPropertyValueMatch = (
  currentValue: string,
  currentId: ObjectId,
  valueToCompare: string,
  valueToCompareId: ObjectId,
): boolean => currentId !== valueToCompareId && currentValue === valueToCompare;

/**
 * Checks if a string property is already present in an array of objects. If so then it will be numbered.
 *
 * @param objArray objects to examine
 * @param valueToValidate property value to validate
 * @param valueToValidateId id of the obj to validate
 * @param getValueToCompareFromObj function to receive the value from object that needs to be unique
 * @param getIdFromObj function to receive the ID value from the object so the examined obj can be filtered out
 * @returns unique name (numbered if needed)
 */
export const checkAndValidateUniquePropertyValueInObjArray = <T>(
  objArray: T[],
  valueToValidate: string,
  valueToValidateId: ObjectId,
  getValueToCompareFromObj: (obj: T) => string,
  getIdFromObj: (obj: T) => ObjectId,
): string => {
  if (
    objArray.some(o =>
      doesPropertyValueMatch(getValueToCompareFromObj(o), getIdFromObj(o), valueToValidate, valueToValidateId),
    )
  ) {
    let counter = 1;
    while (true) {
      const newName = `${valueToValidate} (${counter})`;
      if (
        !objArray.some(o =>
          doesPropertyValueMatch(getValueToCompareFromObj(o), getIdFromObj(o), newName, valueToValidateId),
        )
      ) {
        return newName;
      }

      counter++;
    }
  }

  return valueToValidate;
};

export function setAtIndex<T>(array: T[], index: number, value: T): T[] {
  const copy = Array.from(array);
  copy[index] = value;
  return copy;
}

export const chunk = <T>(input: T[], size: number): T[][] => {
  return input.reduce((arr, item, idx) => {
    return idx % size === 0 ? [...arr, [item]] : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]];
  }, [] as T[][]);
};
